import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTasks } from '../../context/TasksContext';
import { useCompany } from '../../context/CompanyContext';
import { toast } from 'sonner';
import { Ban, CheckCircle2, Loader2, Lock } from 'lucide-react';
import {
  NON_BILLABLE_REASONS,
  requiresNote,
  reasonLabel,
} from '../../lib/billingIntent';

/**
 * NoChargeControl — the PRIMARY, before-completion No Charge override for a
 * service job (WorkOrderSheet). Writes `billingIntent`/`nonBillableReason` to
 * the task via updateTask; nothing financial is created or deleted here.
 *
 * Financial-history protection: if ANY invoice record already exists for this
 * task (deposit/final/paid/outstanding/refunded), flipping to No Charge is
 * blocked — the operator must use the existing invoice/refund/void workflow.
 * Reverting No Charge → Billable is always allowed (a plain field write; it
 * never auto-invoices or auto-charges).
 */
export default function NoChargeControl({ task }) {
  const { updateTask } = useTasks();
  const { companyId } = useCompany();

  const isNonBillable = (task?.billingIntent || '').toLowerCase() === 'non_billable';

  const [hasFinancialHistory, setHasFinancialHistory] = useState(false);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('warranty');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Tenant-scoped, bounded listener for this task's invoices (financial history).
  useEffect(() => {
    if (!companyId || !task?.id) return undefined;
    const col = collection(db, 'companies', companyId, 'invoices');
    const unsub = onSnapshot(
      query(col, where('taskId', '==', task.id)),
      (snap) => setHasFinancialHistory(!snap.empty),
      () => {},
    );
    return () => unsub();
  }, [companyId, task?.id]);

  const setNoCharge = async () => {
    if (hasFinancialHistory) return;
    if (requiresNote(reason) && !note.trim()) {
      toast.error('Add a short note for "Other".');
      return;
    }
    setSaving(true);
    try {
      await updateTask(task.id, {
        billingIntent: 'non_billable',
        nonBillableReason: reason,
        ...(note.trim() ? { nonBillableNote: note.trim() } : {}),
      });
      toast.success('Marked as No Charge — this job will not be billed.');
      setEditing(false);
    } catch {
      toast.error('Could not update billing intent.');
    } finally {
      setSaving(false);
    }
  };

  const setBillable = async () => {
    setSaving(true);
    try {
      await updateTask(task.id, {
        billingIntent: 'billable',
        nonBillableReason: null,
        nonBillableNote: null,
      });
      toast.success('Restored to Billable.');
    } catch {
      toast.error('Could not update billing intent.');
    } finally {
      setSaving(false);
    }
  };

  // ── No Charge currently set ────────────────────────────────────────────
  if (isNonBillable) {
    return (
      <div
        className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2"
        data-testid="no-charge-control"
      >
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
            <Ban className="w-4 h-4" />
            No Charge
            {task?.nonBillableReason ? (
              <span className="text-xs font-normal text-slate-500">
                · {reasonLabel(task.nonBillableReason)}
              </span>
            ) : null}
          </span>
        </div>
        <p className="text-[11px] text-slate-500 leading-relaxed">
          This job will complete operationally with no invoice or charge.
        </p>
        <button
          type="button"
          onClick={setBillable}
          disabled={saving}
          data-testid="no-charge-restore-billable-btn"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:underline disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Restore to Billable
        </button>
      </div>
    );
  }

  // ── Billable (default) — offer the No Charge override ───────────────────
  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-3 space-y-2"
      data-testid="no-charge-control"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Billing</span>
        <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-emerald-50 text-emerald-700">
          Billable
        </span>
      </div>

      {hasFinancialHistory ? (
        <p className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 leading-relaxed">
          <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          This job has invoice or payment records. Use the Payments workflow to adjust billing — No Charge is locked.
        </p>
      ) : !editing ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid="no-charge-open-btn"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 hover:underline"
        >
          <Ban className="w-3.5 h-3.5" />
          Mark as No Charge
        </button>
      ) : (
        <div className="space-y-2">
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="no-charge-reason-select"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            {NON_BILLABLE_REASONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          {requiresNote(reason) && (
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a short note"
              data-testid="no-charge-note-input"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={setNoCharge}
              disabled={saving}
              data-testid="no-charge-confirm-btn"
              className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-slate-700 hover:bg-slate-800 rounded-lg px-3 py-2 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
              Confirm No Charge
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setNote(''); }}
              disabled={saving}
              className="text-xs font-medium text-gray-500 hover:text-gray-700 px-3 py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
